/**
 * Phase 6.0 — Founder's Thanks page.
 *
 * Super-admin only tool that bundles bulk points award + push + optional
 * email to a filtered customer cohort. Originally built for the pilot
 * launch thank-you, reusable for any milestone moment (holiday bonuses,
 * apology credits, tier upgrades).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { ArrowLeft, Sparkles, Send, BeakerIcon, Heart, Loader2, AlertTriangle, Zap, BookmarkPlus, Trash2 } from 'lucide-react';
import { Navbar } from '../../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FoundersThanksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Cohort filter
  const [region, setRegion] = useState('all');
  const [hasDeliveredMin, setHasDeliveredMin] = useState(0);
  const [signedUpBefore, setSignedUpBefore] = useState('');
  // Phase 7-lite — target users by app-login status. 'any' | 'has_signed_in' | 'never_signed_in'
  const [loginFilter, setLoginFilter] = useState('any');

  // Resolved recipients + selection
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rateCheck, setRateCheck] = useState(null);

  // Composer
  const [points, setPoints] = useState(100);
  const [reason, setReason] = useState('');
  const [pushTitle, setPushTitle] = useState('💚 Thanks for being a pilot customer');
  const [pushBody, setPushBody] = useState('We just credited 100 pts to your account!');
  const [includeEmail, setIncludeEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState('A little thank-you from Ash & Merch 💚');
  const [emailBody, setEmailBody] = useState(
    '<p>Hey {first_name},</p><p>You\'re one of our first pilot customers and we just wanted to say <strong>thank you</strong>. We credited <strong>100 pts</strong> to your account to use on your next order.</p><p>— The Ash &amp; Merch team</p>',
  );
  // Phase 7.1 — optional single CTA button in the email body. Adds UTM +
  // recipient_id params at send time so we can attribute clicks per-recipient.
  const [ctaLabel, setCtaLabel] = useState('> USE YOUR BONUS POINTS');
  const [ctaUrl, setCtaUrl] = useState('');

  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewRecipientId, setPreviewRecipientId] = useState(null);
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState({});  // {grant_id: {sent,delivered,opened,clicked,...}}
  const [analyticsLoading, setAnalyticsLoading] = useState({});
  // Phase 6.4 — Failure diagnostic: {grant_id: {error_summary, email_failed, recipients_with_errors}}
  const [diagnostic, setDiagnostic] = useState({});
  const [diagnosticLoading, setDiagnosticLoading] = useState({});

  // Phase 6.0.4 — Template support. Shared store (`broadcast_templates`)
  // already exists for the push-only broadcast composer; BOOST templates
  // extend the same rows with `channels=["push","email","points"]` plus
  // email_subject / email_body_html / cta_label / cta_url / points / reason.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Phase 6.1.0 — Scheduled send. `sendMode` is "now" or "schedule"; when
  // "schedule", `scheduledLocal` is a datetime-local string ("YYYY-MM-DDTHH:MM")
  // in the operator's local timezone. We convert to UTC ISO at send time.
  const [sendMode, setSendMode] = useState('now');
  const [scheduledLocal, setScheduledLocal] = useState('');
  const [scheduledList, setScheduledList] = useState([]);

  const isSuper = user?.role === 'super_admin';

  // Initial cohort load.
  useEffect(() => {
    if (isSuper) {
      fetchPreview();
      fetchHistory();
      fetchTemplates();
      fetchScheduled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  // Phase 6.1.1 — Deep-link cohort pre-fill (e.g. /dispatch/admin/boost?cohort=dormant
  // launched from the Dormant 30D+ tile on Login Stats). When present, sets the
  // login filter + re-fetches the cohort preview so the recipient list reflects
  // the new filter immediately (otherwise the initial mount fetch used 'any').
  useEffect(() => {
    if (!isSuper) return;
    const params = new URLSearchParams(window.location.search);
    const cohort = params.get('cohort');
    if (cohort === 'dormant') {
      setLoginFilter('dormant_30d');
      toast.success('Dormant 30D+ cohort selected · pick a template to start');
    } else if (cohort === 'never') {
      setLoginFilter('never_signed_in');
      toast.success('Never-returned cohort selected');
    }
    if (cohort) {
      // Strip query so refresh doesn't re-trigger the toast
      window.history.replaceState({}, '', window.location.pathname);
      // Re-fetch the cohort preview with the new filter applied. Delay so the
      // setState above has flushed.
      setTimeout(() => fetchPreview(), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  // Phase 6.1.0 — Scheduled BOOST helpers
  const fetchScheduled = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/founders-thanks/scheduled`,
        { withCredentials: true },
      );
      setScheduledList(data.scheduled || []);
    } catch (_) { /* silent */ }
  };

  const cancelScheduled = async (schedId, preview) => {
    if (!window.confirm(`Cancel scheduled BOOST to ${preview.recipients_count} recipients?`)) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/founders-thanks/scheduled/${schedId}/cancel`,
        {},
        { withCredentials: true },
      );
      toast.success('Cancelled');
      await fetchScheduled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    }
  };

  // Phase 6.1.0 — Peak-time chip helper. Returns a "YYYY-MM-DDTHH:MM"
  // string for the next occurrence of a target weekday/hour in operator's
  // local timezone (matches the <input type="datetime-local"> format).
  const nextDateAt = (weekday /* 0=Sun..6=Sat */, hour) => {
    const now = new Date();
    const d = new Date(now);
    const diff = (weekday + 7 - d.getDay()) % 7;
    d.setDate(d.getDate() + (diff === 0 && now.getHours() >= hour ? 7 : diff));
    d.setHours(hour, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Phase 6.0.4 — Template helpers
  const fetchTemplates = async () => {
    try {
      // channel=email → only multi-channel BOOST templates (filters out
      // push-only broadcast templates which don't have email content)
      const { data } = await axios.get(
        `${API_URL}/api/push/broadcast/templates?channel=email`,
        { withCredentials: true },
      );
      setTemplates(data.templates || []);
    } catch (_) { /* silent */ }
  };

  const loadTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    setPushTitle(t.title || '');
    setPushBody(t.body || '');
    if (t.audience) {/* audience not used in BOOST cohort filter; ignore */}
    if (t.email_subject) {
      setIncludeEmail(true);
      setEmailSubject(t.email_subject);
      setEmailBody(t.email_body_html || '');
    }
    if (t.cta_label || t.cta_url) {
      setCtaLabel(t.cta_label || '');
      setCtaUrl(t.cta_url || '');
    }
    if (t.points && t.points > 0) setPoints(t.points);
    if (t.reason) setReason(t.reason);
    toast.success(`Loaded "${t.name}"`);
  };

  const saveAsTemplate = async () => {
    const name = (saveTemplateName || '').trim();
    if (!name) { toast.error('Template name required'); return; }
    setSavingTemplate(true);
    try {
      const payload = {
        name,
        title: pushTitle,
        body: pushBody,
        audience: 'customer',
        url: null,
        channels: ['push', ...(includeEmail ? ['email'] : []), ...(points > 0 ? ['points'] : [])],
        email_subject: includeEmail ? emailSubject : null,
        email_body_html: includeEmail ? emailBody : null,
        cta_label: includeEmail ? (ctaLabel || null) : null,
        cta_url: includeEmail ? (ctaUrl || null) : null,
        points: points > 0 ? points : null,
        reason: points > 0 ? reason : null,
      };
      await axios.post(`${API_URL}/api/push/broadcast/templates`, payload, { withCredentials: true });
      toast.success(`Saved template "${name}"`);
      setSaveTemplateOpen(false);
      setSaveTemplateName('');
      await fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/push/broadcast/templates/${templateId}`, { withCredentials: true });
      toast.success('Template deleted');
      if (selectedTemplateId === templateId) setSelectedTemplateId('');
      await fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const fetchPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/founders-thanks/preview`, {
        cohort: {
          region,
          has_delivered_min: Number(hasDeliveredMin) || 0,
          signed_up_before: signedUpBefore || null,
          login_filter: loginFilter === 'any' ? null : loginFilter,
        },
      }, { withCredentials: true });
      setRecipients(data.recipients || []);
      // Default-select everyone matched.
      setSelectedIds(new Set((data.recipients || []).map((r) => r.id)));
      setRateCheck(data.rate_check || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load cohort');
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/founders-thanks/history?limit=10`, { withCredentials: true });
      setHistory(data.grants || []);
    } catch (_) { /* silent */ }
  };

  const fetchAnalytics = async (grantId) => {
    setAnalyticsLoading((p) => ({ ...p, [grantId]: true }));
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/founders-thanks/analytics/${grantId}`, { withCredentials: true });
      setAnalytics((p) => ({ ...p, [grantId]: data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setAnalyticsLoading((p) => ({ ...p, [grantId]: false }));
    }
  };

  // Phase 6.4 — Diagnose why some recipients didn't get the email/push.
  // Pulls per_recipient errors[] + histogram from the new detail endpoint.
  const fetchDiagnostic = async (grantId) => {
    setDiagnosticLoading((p) => ({ ...p, [grantId]: true }));
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/founders-thanks/history/${grantId}`, { withCredentials: true });
      setDiagnostic((p) => ({ ...p, [grantId]: data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load diagnostic');
    } finally {
      setDiagnosticLoading((p) => ({ ...p, [grantId]: false }));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === recipients.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(recipients.map((r) => r.id)));
  };

  const buildPayload = () => ({
    cohort: {
      region,
      has_delivered_min: Number(hasDeliveredMin) || 0,
      signed_up_before: signedUpBefore || null,
      login_filter: loginFilter === 'any' ? null : loginFilter,
    },
    recipient_ids: Array.from(selectedIds),
    points: Number(points) || 0,
    reason,
    push: { title: pushTitle, body: pushBody },
    email: includeEmail ? {
      subject: emailSubject,
      body_html: emailBody,
      cta: (ctaLabel.trim() && ctaUrl.trim()) ? { label: ctaLabel.trim(), url: ctaUrl.trim() } : null,
    } : null,
    // Phase 6.0.5 — Stamp template lineage on the resulting grant so analytics
    // can answer "which BOOST template converts best".
    template_id: selectedTemplateId || null,
    // Phase 6.1.0 — When sendMode='schedule', convert the local datetime-local
    // value to UTC ISO. Server validates >=15min future and <=14d future.
    scheduled_at: (sendMode === 'schedule' && scheduledLocal)
      ? new Date(scheduledLocal).toISOString()
      : null,
  });

  const testSend = async () => {
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/admin/founders-thanks/test-send`, buildPayload(), { withCredentials: true });
      toast.success('Test sent to you — check your phone + email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  const openConfirm = () => {
    if (selectedIds.size === 0) return toast.error('No recipients selected');
    // Default preview recipient = first selected (matches backend ordering).
    const firstSelected = recipients.find((r) => selectedIds.has(r.id));
    setPreviewRecipientId(firstSelected ? firstSelected.id : null);
    setConfirmOpen(true);
  };

  const executeSend = async () => {
    setConfirmOpen(false);
    const n = selectedIds.size;
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/founders-thanks/send`, buildPayload(), { withCredentials: true });
      if (data.status === 'scheduled') {
        const when = new Date(data.scheduled_at).toLocaleString();
        toast.success(`⏰ Scheduled for ${when} · ${n} recipients`);
        // Reset schedule fields so the form is fresh after a successful schedule
        setSendMode('now');
        setScheduledLocal('');
        await fetchScheduled();
      } else {
        const t = data.totals || {};
        toast.success(
          `Sent! ${t.points_credited}/${n} pts · ${t.push_delivered}/${n} push · ${t.email_delivered || 0}/${n} email`,
        );
        fetchHistory();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const totalCost = useMemo(() => selectedIds.size * (Number(points) || 0), [selectedIds, points]);

  // Soft preview — mirror server-side templating from /backend/founders_thanks.py
  // so the operator sees exactly what the recipient will receive.
  const personalize = (tpl, fullName) => {
    if (!tpl) return tpl;
    const first = (fullName || 'there').split(' ')[0];
    return tpl.replace(/\{first_name\}/g, first).replace(/\{name\}/g, fullName || 'there');
  };
  const toHtmlIfNeeded = (body) => {
    if (!body) return '';
    const b = body.trim();
    const low = b.toLowerCase();
    if (low.includes('<html') || low.includes('<p>') || low.includes('<br') || low.includes('<div')) return body;
    const paras = b.split(/\n\n/).map((p) => p.trim().replace(/\n/g, '<br>')).filter(Boolean);
    return paras
      .map((p) => `<p style="margin:0 0 14px;line-height:1.55;color:#A3C7A8;font-family:-apple-system,sans-serif;font-size:14px;">${p}</p>`)
      .join('\n');
  };
  const previewRecipient = useMemo(
    () => recipients.find((r) => r.id === previewRecipientId) || null,
    [recipients, previewRecipientId],
  );
  const previewName = previewRecipient?.name || 'there';
  const previewPushTitle = personalize(pushTitle, previewName);
  const previewPushBody = personalize(pushBody, previewName);
  const previewEmailSubject = personalize(emailSubject, previewName);
  // CTA preview: build a fake but realistic UTM-wrapped URL so operator sees
  // the actual tracking pattern that will hit each recipient's inbox.
  const previewCtaUrl = useMemo(() => {
    if (!ctaUrl.trim()) return '';
    try {
      const u = new URL(ctaUrl.trim());
      if (!u.searchParams.has('utm_source')) u.searchParams.set('utm_source', 'founders_thanks');
      if (!u.searchParams.has('utm_medium')) u.searchParams.set('utm_medium', 'email');
      if (!u.searchParams.has('utm_campaign')) u.searchParams.set('utm_campaign', 'preview');
      u.searchParams.set('recipient_id', previewRecipientId || 'preview');
      return u.toString();
    } catch (_) {
      return ctaUrl.trim();
    }
  }, [ctaUrl, previewRecipientId]);
  const previewEmailHtml = useMemo(() => {
    let html = toHtmlIfNeeded(personalize(emailBody, previewName));
    if (ctaLabel.trim() && previewCtaUrl) {
      const safeLabel = ctaLabel.trim().replace(/</g, '&lt;');
      const btn = `<p style="margin:24px 0 8px;text-align:center;">
<a href="${previewCtaUrl}" style="background:#39FF14;color:#050003;padding:14px 28px;text-decoration:none;font-weight:bold;display:inline-block;font-family:monospace;letter-spacing:1px;border-radius:4px;">${safeLabel}</a></p>`;
      const marker = '</div></div>';
      const idx = html.lastIndexOf(marker);
      html = idx >= 0 ? html.slice(0, idx) + btn + html.slice(idx) : html + btn;
    }
    return html;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailBody, previewName, ctaLabel, previewCtaUrl]);

  if (!isSuper) {
    return (
      <div className="min-h-screen bg-[#050003] text-[#39FF14] p-8 font-mono dispatch-safe-top">
        <p>Super-admin only.</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#050003] text-[#39FF14] font-mono"
      data-testid="boost-page"
    >
      <Navbar />
      <div className="p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/dispatch')}
            className="text-[#A3C7A8] hover:text-[#39FF14] text-xs uppercase tracking-wider flex items-center gap-1"
            data-testid="back-to-dispatch"
          >
            <ArrowLeft className="w-3 h-3" /> Dispatch
          </button>
          <h1 className="text-lg uppercase tracking-wider flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#39FF14]" /> BOOST_
          </h1>
          <div className="w-20" />
        </div>

        {/* Rate-limit warning */}
        {rateCheck && !rateCheck.ok && (
          <div className="border border-[#FF3B30] text-[#FF3B30] p-3 text-xs">
            ⚠ {rateCheck.reason}
          </div>
        )}

        {/* Phase 6.0.4 — Template picker. Saves/loads multi-channel BOOST
            templates (push + email + points + CTA) from the shared
            broadcast_templates store. */}
        <section
          className="border border-[#39FF14] bg-[#0C0612] p-3 sm:p-4 flex flex-col sm:flex-row sm:items-end gap-3"
          data-testid="boost-template-picker"
        >
          <div className="flex-1">
            <Label className="text-[#39FF14] text-[10px] uppercase tracking-wider flex items-center gap-1">
              <BookmarkPlus className="w-3 h-3" /> Load a saved boost
            </Label>
            <div className="flex gap-2 mt-1">
              <Select value={selectedTemplateId || '__none__'} onValueChange={(v) => loadTemplate(v === '__none__' ? '' : v)}>
                <SelectTrigger
                  className="bg-[#050003] border-[#39FF14] text-[#39FF14] rounded-none flex-1"
                  data-testid="template-select"
                >
                  <SelectValue placeholder="// Choose a template…" />
                </SelectTrigger>
                <SelectContent className="bg-[#050003] border-[#39FF14] text-[#39FF14]">
                  <SelectItem value="__none__">// none — compose from scratch</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                      {t.name}
                      {t.points ? ` · ${t.points} pts` : ''}
                      {t.cta_label ? ' · cta' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplateId && (
                <button
                  type="button"
                  onClick={() => {
                    const t = templates.find((x) => x.id === selectedTemplateId);
                    if (t) deleteTemplate(t.id, t.name);
                  }}
                  className="border border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] px-3 text-xs"
                  data-testid="template-delete-btn"
                  title="Delete this template"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
            {templates.length === 0 && (
              <p className="text-[#6B8E73] text-[10px] mt-1">
                // No saved templates yet. Compose a BOOST below, then click &quot;Save as Template&quot;.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setSaveTemplateOpen(true)}
            className="bg-[#39FF14] text-[#050003] hover:bg-[#7FFF55] px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2"
            data-testid="save-template-btn"
          >
            <BookmarkPlus className="w-3 h-3" /> Save as Template
          </button>
        </section>

        {/* Cohort */}
        <section className="border border-[#2A0F3D] bg-[#0C0612] p-3 sm:p-4 space-y-3">
          <h2 className="text-xs uppercase tracking-wider text-[#39FF14]">{'>'} COHORT FILTER</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-[#6B8E73] text-[10px] uppercase">Region</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  <SelectItem value="dmv">DMV</SelectItem>
                  <SelectItem value="nj">New Jersey</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#6B8E73] text-[10px] uppercase">Has delivered ≥</Label>
              <Input
                type="number"
                min="0"
                value={hasDeliveredMin}
                onChange={(e) => setHasDeliveredMin(e.target.value)}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1"
                data-testid="cohort-delivered-min"
              />
            </div>
            <div>
              <Label className="text-[#6B8E73] text-[10px] uppercase">Signed up before</Label>
              <Input
                type="date"
                value={signedUpBefore}
                onChange={(e) => setSignedUpBefore(e.target.value)}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1"
                data-testid="cohort-signed-before"
              />
            </div>
            <div>
              <Label className="text-[#6B8E73] text-[10px] uppercase">App-login status</Label>
              <Select value={loginFilter} onValueChange={setLoginFilter}>
                <SelectTrigger
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1"
                  data-testid="cohort-login-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any (no filter)</SelectItem>
                  <SelectItem value="has_signed_in">Has signed in</SelectItem>
                  <SelectItem value="never_signed_in">Never signed in (dormant)</SelectItem>
                  <SelectItem value="dormant_30d">Dormant 30D+ (returned then silent)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={fetchPreview}
            disabled={previewLoading}
            className="bg-[#2A0F3D] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none text-[10px] uppercase tracking-wider"
            data-testid="refresh-cohort-btn"
          >
            {previewLoading ? 'LOADING…' : 'REFRESH COHORT'}
          </Button>
        </section>

        {/* Recipients */}
        <section className="border border-[#2A0F3D] bg-[#0C0612] p-3 sm:p-4 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-xs uppercase tracking-wider text-[#39FF14]">
              {'>'} RECIPIENTS ({selectedIds.size}/{recipients.length})
            </h2>
            <button
              onClick={toggleAll}
              className="text-[10px] text-[#7FD8FF] uppercase hover:underline"
              data-testid="toggle-all-recipients"
            >
              {selectedIds.size === recipients.length && recipients.length > 0 ? 'DESELECT ALL' : 'SELECT ALL'}
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto border border-[#2A0F3D]">
            {recipients.length === 0 && (
              <p className="p-3 text-[#4B6B52] text-xs">No customers matched. Adjust the filter and refresh.</p>
            )}
            {recipients.map((r) => (
              <label key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-xs border-b border-[#2A0F3D]/50 hover:bg-[#1A0A28] cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.has(r.id)}
                  onChange={() => toggleOne(r.id)}
                  className="accent-[#39FF14]"
                  data-testid={`recipient-checkbox-${r.id}`}
                />
                <span className="text-[#A3C7A8] flex-1 truncate">
                  {/* Phase 5.9.29 — click name to open user profile (stop bubble so checkbox doesn't toggle) */}
                  <Link
                    to={`/dispatch/admin/users?openUser=${r.id}&from=founders-thanks`}
                    className="hover:underline hover:text-[#39FF14]"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`recipient-name-link-${r.id}`}
                  >
                    {r.name}
                  </Link>
                </span>
                <span className="text-[#6B8E73] hidden sm:inline truncate flex-1">{r.email}</span>
                <span className="text-[#7FD8FF] text-[10px] uppercase">{r.region || '—'}</span>
                <span className="text-[#39FF14] text-[10px] whitespace-nowrap">{r.delivered_count} ord</span>
              </label>
            ))}
          </div>
        </section>

        {/* Points + reason */}
        <section className="border border-[#2A0F3D] bg-[#0C0612] p-3 sm:p-4 space-y-3">
          <h2 className="text-xs uppercase tracking-wider text-[#39FF14]">{'>'} POINTS</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-[#6B8E73] text-[10px] uppercase">Pts each</Label>
              <Input
                type="number"
                min="1"
                max="1000"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1"
                data-testid="points-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#6B8E73] text-[10px] uppercase">Reason (visible to customer)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1"
                data-testid="reason-input"
                placeholder="Thanks for being a pilot customer"
              />
              <p className="text-[#6B8E73] text-[10px] mt-1">
                Shown to each recipient in their points ledger — write it like a public note.
              </p>
            </div>
          </div>
          <p className="text-[#FF9F0A] text-[10px]">
            Total ledger cost: <strong>{totalCost} pts</strong> ({selectedIds.size} × {points})
          </p>
        </section>

        {/* Push */}
        <section className="border border-[#2A0F3D] bg-[#0C0612] p-3 sm:p-4 space-y-3">
          <h2 className="text-xs uppercase tracking-wider text-[#39FF14]">{'>'} PUSH NOTIFICATION</h2>
          <div>
            <Label className="text-[#6B8E73] text-[10px] uppercase">Title</Label>
            <Input
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1"
              data-testid="push-title-input"
            />
          </div>
          <div>
            <Label className="text-[#6B8E73] text-[10px] uppercase">Body</Label>
            <Textarea
              value={pushBody}
              onChange={(e) => setPushBody(e.target.value)}
              rows={2}
              className="bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] rounded-none mt-1 resize-none"
              data-testid="push-body-input"
            />
          </div>
        </section>

        {/* Email (optional) */}
        <section className="border border-[#2A0F3D] bg-[#0C0612] p-3 sm:p-4 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeEmail}
              onChange={(e) => setIncludeEmail(e.target.checked)}
              className="accent-[#39FF14]"
              data-testid="include-email-toggle"
            />
            <h2 className="text-xs uppercase tracking-wider text-[#39FF14]">{'>'} ALSO SEND EMAIL</h2>
          </label>
          {includeEmail && (
            <>
              <div>
                <Label className="text-[#6B8E73] text-[10px] uppercase">Subject</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1"
                  data-testid="email-subject-input"
                />
              </div>
              <div>
                <Label className="text-[#6B8E73] text-[10px] uppercase">Body (HTML)</Label>
                <Textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={6}
                  className="bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] rounded-none mt-1 resize-none text-xs"
                  data-testid="email-body-input"
                />
              </div>

              {/* Optional single CTA — injected as a styled button at end of email */}
              <div className="border-t border-dashed border-[#2A0F3D] pt-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-[10px] uppercase tracking-wider text-[#7FD8FF]">{'>'} OPTIONAL CTA BUTTON</h3>
                  <span className="text-[9px] text-[#A3C7A8]/50">UTM + recipient_id auto-appended</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[#6B8E73] text-[10px] uppercase">Button label</Label>
                    <Input
                      value={ctaLabel}
                      onChange={(e) => setCtaLabel(e.target.value)}
                      placeholder="> USE YOUR BONUS POINTS"
                      className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1 text-xs"
                      data-testid="cta-label-input"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[#6B8E73] text-[10px] uppercase">Destination URL</Label>
                    <Input
                      value={ctaUrl}
                      onChange={(e) => setCtaUrl(e.target.value)}
                      placeholder="https://ashandmerch.com/customer"
                      className="bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] rounded-none mt-1 text-xs"
                      data-testid="cta-url-input"
                    />
                  </div>
                </div>
                {(!ctaUrl.trim() || !ctaLabel.trim()) && (
                  <p className="text-[9px] text-[#A3C7A8]/50 italic">
                    Leave blank for no CTA. Fill both fields to inject a tracked button — clicks land in <code>/dispatch/admin/founders-thanks</code> analytics.
                  </p>
                )}
              </div>
            </>
          )}
        </section>

        {/* Actions — two separated zones to prevent destructive mis-clicks */}
        <section className="space-y-4">
          {/* Zone 1: TEST SEND — small, safe, secondary */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Button
              onClick={testSend}
              disabled={sending}
              variant="outline"
              className="sm:w-auto border-[#2A0F3D] bg-transparent text-[#7FD8FF] hover:bg-[#7FD8FF]/10 hover:text-[#7FD8FF] rounded-none font-mono uppercase tracking-wider"
              data-testid="test-send-btn"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SENDING…</>
              ) : (
                <><BeakerIcon className="w-4 h-4 mr-2" /> TEST SEND TO ME</>
              )}
            </Button>
            <p className="text-[10px] text-[#A3C7A8]/70 leading-tight">
              Sends a single push + email to <span className="text-[#7FD8FF]">your</span> account only. Safe to retry.
            </p>
          </div>

          {/* Visual separator between safe + destructive actions */}
          <div className="border-t border-dashed border-[#2A0F3D]" />

          {/* Zone 2: SEND TO RECIPIENTS — destructive bulk action */}
          <div className="space-y-2">
            {(() => {
              // Resolve disabled-reason caption for operator clarity.
              let reasonText = null;
              if (sending) reasonText = 'Sending in progress — please wait…';
              else if (selectedIds.size === 0) reasonText = 'Select at least one recipient above to enable.';
              else if (rateCheck && !rateCheck.ok) reasonText = `Rate-limited: ${rateCheck.reason}`;
              const isDisabled = sending || selectedIds.size === 0 || (rateCheck && !rateCheck.ok);
              return (
                <>
                  {/* Phase 6.1.0 — Send mode + scheduled-for picker */}
                  <div className="border border-[#2A0F3D] p-3 sm:p-4 space-y-3" data-testid="boost-send-mode">
                    <div className="flex items-center gap-3">
                      <Label className="text-[#6B8E73] text-[10px] uppercase tracking-wider shrink-0">When:</Label>
                      <div className="flex border border-[#2A0F3D]">
                        <button
                          type="button"
                          onClick={() => setSendMode('now')}
                          className={`px-3 py-1 text-xs font-mono ${sendMode === 'now' ? 'bg-[#39FF14] text-[#050003] font-bold' : 'text-[#6B8E73] hover:text-[#A3C7A8]'}`}
                          data-testid="send-mode-now"
                        >
                          SEND NOW
                        </button>
                        <button
                          type="button"
                          onClick={() => setSendMode('schedule')}
                          className={`px-3 py-1 text-xs font-mono border-l border-[#2A0F3D] ${sendMode === 'schedule' ? 'bg-[#FFA500] text-[#050003] font-bold' : 'text-[#6B8E73] hover:text-[#A3C7A8]'}`}
                          data-testid="send-mode-schedule"
                        >
                          ⏰ SCHEDULE
                        </button>
                      </div>
                    </div>
                    {sendMode === 'schedule' && (
                      <div className="space-y-2">
                        <Input
                          type="datetime-local"
                          value={scheduledLocal}
                          onChange={(e) => setScheduledLocal(e.target.value)}
                          className="bg-[#050003] border-[#FFA500] text-[#FFA500] rounded-none font-mono"
                          data-testid="schedule-datetime-input"
                        />
                        <div className="flex flex-wrap gap-1.5" data-testid="peak-time-chips">
                          <span className="text-[#6B8E73] text-[10px] mr-1 self-center">// peak times:</span>
                          {[
                            { label: 'Fri 5pm', wday: 5, hr: 17, tip: '48% avg open rate' },
                            { label: 'Thu 11am', wday: 4, hr: 11, tip: '42% avg open rate' },
                            { label: 'Sat 10am', wday: 6, hr: 10, tip: '40% avg open rate' },
                          ].map((c) => (
                            <button
                              key={c.label}
                              type="button"
                              onClick={() => setScheduledLocal(nextDateAt(c.wday, c.hr))}
                              className="border border-[#FFA500] text-[#FFA500] hover:bg-[rgba(255,165,0,0.08)] text-[10px] font-mono px-2 py-1 uppercase tracking-wider"
                              title={c.tip}
                              data-testid={`peak-chip-${c.label.replace(/\s+/g,'-').toLowerCase()}`}
                            >
                              {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={openConfirm}
                    disabled={isDisabled || (sendMode === 'schedule' && !scheduledLocal)}
                    className={`w-full h-14 ${sendMode === 'schedule' ? 'bg-[#FFA500] hover:bg-[#FFB341]' : 'bg-[#39FF14] hover:bg-[#4AF626]'} text-[#050003] disabled:bg-[#2A0F3D] disabled:text-[#A3C7A8]/40 rounded-none font-mono font-bold uppercase tracking-wider text-base`}
                    data-testid="send-for-real-btn"
                  >
                    {sending ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {sendMode === 'schedule' ? 'SCHEDULING' : 'SENDING'} TO {selectedIds.size}…</>
                    ) : sendMode === 'schedule' ? (
                      <>⏰ SCHEDULE FOR {selectedIds.size} RECIPIENTS</>
                    ) : (
                      <><Send className="w-5 h-5 mr-2" /> ★ SEND TO {selectedIds.size} RECIPIENTS</>
                    )}
                  </Button>
                  {reasonText && (
                    <p
                      className="text-[10px] text-[#FFB020] flex items-center gap-1 leading-tight"
                      data-testid="send-disabled-reason"
                    >
                      <AlertTriangle className="w-3 h-3 shrink-0" /> {reasonText}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </section>

        {/* Confirmation dialog — replaces the old window.confirm */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent
            className="bg-[#0C0612] border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="send-confirm-dialog"
          >
            <DialogHeader>
              <DialogTitle className="text-[#39FF14] uppercase tracking-wider text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#FFB020]" /> Confirm bulk send
              </DialogTitle>
              <DialogDescription className="text-[#A3C7A8]/70 text-xs pt-2">
                This will credit points and notify <span className="text-[#39FF14]">{selectedIds.size}</span> customers. It cannot be undone.
              </DialogDescription>
            </DialogHeader>

            {/* Summary grid */}
            <div className="text-xs space-y-1 border border-[#2A0F3D] p-3">
              <div><span className="text-[#A3C7A8]/60">Recipients:</span> <span className="text-[#39FF14]">{selectedIds.size}</span></div>
              <div><span className="text-[#A3C7A8]/60">Points each:</span> <span className="text-[#39FF14]">{points}</span></div>
              <div><span className="text-[#A3C7A8]/60">Total ledger cost:</span> <span className="text-[#39FF14]">{totalCost} pts</span></div>
              <div><span className="text-[#A3C7A8]/60">Channels:</span> <span className="text-[#7FD8FF]">push{includeEmail ? ' + email' : ''}</span></div>
              {reason && <div><span className="text-[#A3C7A8]/60">Reason:</span> <span className="text-[#A3C7A8]">{reason}</span></div>}
            </div>

            {/* Soft preview — render the actual templated push + email for one recipient */}
            <div className="border border-[#2A0F3D] p-3 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-[10px] uppercase tracking-wider text-[#39FF14]">
                  {'>'} Soft preview — exactly what they&apos;ll receive
                </h3>
                <div className="flex items-center gap-2">
                  <Label className="text-[9px] text-[#6B8E73] uppercase">Preview as</Label>
                  <select
                    value={previewRecipientId || ''}
                    onChange={(e) => setPreviewRecipientId(e.target.value)}
                    className="bg-[#050003] border border-[#2A0F3D] text-[#A3C7A8] text-[10px] px-2 py-1 rounded-none font-mono max-w-[200px]"
                    data-testid="preview-recipient-select"
                  >
                    {recipients
                      .filter((r) => selectedIds.has(r.id))
                      .map((r) => (
                        <option key={r.id} value={r.id}>{r.name} ({r.email})</option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Push preview — styled like an iOS/Android notification banner */}
              <div className="space-y-1">
                <div className="text-[9px] text-[#6B8E73] uppercase">📱 Push notification</div>
                <div
                  className="bg-[#1A1A1C] border border-[#3A3A3C] rounded-md p-2 flex items-start gap-2"
                  data-testid="preview-push"
                  style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}
                >
                  <div className="w-7 h-7 bg-[#39FF14] rounded-md flex items-center justify-center shrink-0">
                    <Heart className="w-3.5 h-3.5 text-[#050003]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[10px] text-[#A3C7A8]/70 uppercase tracking-wide">Ash & Merch</span>
                      <span className="text-[9px] text-[#A3C7A8]/50">now</span>
                    </div>
                    <div className="text-[12px] text-white font-semibold truncate" data-testid="preview-push-title">
                      {previewPushTitle || <span className="text-[#FF3B30]">[empty title]</span>}
                    </div>
                    <div className="text-[11px] text-[#D6D6D6] line-clamp-2" data-testid="preview-push-body">
                      {previewPushBody || <span className="text-[#FF3B30]">[empty body]</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email preview — only if includeEmail is on */}
              {includeEmail ? (
                <div className="space-y-1">
                  <div className="text-[9px] text-[#6B8E73] uppercase">✉️ Email</div>
                  <div className="bg-white text-[#1A1A1C] border border-[#2A0F3D] rounded-sm overflow-hidden">
                    <div className="px-3 py-2 border-b border-[#E5E5E5] text-[11px] font-semibold" data-testid="preview-email-subject">
                      <span className="text-[#6B7280] font-normal">Subject:</span> {previewEmailSubject || <span className="text-[#FF3B30]">[empty subject]</span>}
                    </div>
                    <div
                      className="p-3 max-h-64 overflow-y-auto"
                      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}
                      data-testid="preview-email-body"
                      dangerouslySetInnerHTML={{ __html: previewEmailHtml || '<p style="color:#FF3B30">[empty body]</p>' }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-[10px] text-[#A3C7A8]/50 italic">
                  ✉️ Email channel is off — toggle &ldquo;Also send email&rdquo; to include + preview.
                </div>
              )}

              {(pushTitle.includes('{first_name}') || pushBody.includes('{first_name}') || emailSubject.includes('{first_name}') || emailBody.includes('{first_name}')) && (
                <div className="text-[9px] text-[#7FD8FF] flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> <code>{'{first_name}'}</code> replaced with <span className="text-[#39FF14]">&ldquo;{(previewName || 'there').split(' ')[0]}&rdquo;</span> for this preview — each recipient sees their own.
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                onClick={() => setConfirmOpen(false)}
                variant="outline"
                className="border-[#2A0F3D] bg-transparent text-[#A3C7A8] hover:bg-[#2A0F3D]/30 rounded-none font-mono uppercase tracking-wider text-xs"
                data-testid="confirm-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={executeSend}
                className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none font-mono font-bold uppercase tracking-wider text-xs"
                data-testid="confirm-send-btn"
              >
                <Send className="w-3 h-3 mr-2" /> Yes, send to {selectedIds.size}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 6.1.0 — Upcoming scheduled BOOSTs (cancel before fire) */}
        {scheduledList.length > 0 && (
          <section className="border border-[#FFA500] bg-[#0C0612] p-3 sm:p-4 space-y-2" data-testid="upcoming-boosts-section">
            <h2 className="text-xs uppercase tracking-wider text-[#FFA500] flex items-center gap-2">
              <span>⏰ UPCOMING SENDS ({scheduledList.length})</span>
            </h2>
            <ul className="divide-y divide-[#2A0F3D] border border-[#2A0F3D]">
              {scheduledList.map((s) => {
                const when = new Date(s.scheduled_at);
                return (
                  <li key={s.id} className="p-3 flex items-start justify-between gap-3" data-testid={`scheduled-row-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[#FFA500] font-mono text-xs font-bold">
                        ⏰ {when.toLocaleString()}
                      </p>
                      <p className="text-[#A3C7A8] text-xs mt-1 truncate">
                        {s.preview?.push_title}
                        {s.preview?.email_subject ? <span className="text-[#6B8E73]"> · email: {s.preview.email_subject}</span> : null}
                      </p>
                      <p className="text-[#6B8E73] text-[10px] mt-1">
                        {s.preview?.recipients_count} recipients · {s.preview?.points} pts each · by {s.actor_email}
                        {s.preview?.template_name && (
                          <span className="text-[#39FF14]"> · from: {s.preview.template_name}</span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => cancelScheduled(s.id, s.preview || {})}
                      className="border border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] px-3 py-1 text-[10px] uppercase tracking-wider font-mono shrink-0"
                      data-testid={`cancel-scheduled-${s.id}`}
                    >
                      Cancel
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* History */}
        {history.length > 0 && (
          <section className="border border-[#2A0F3D] bg-[#0C0612] p-3 sm:p-4 space-y-2">
            <h2 className="text-xs uppercase tracking-wider text-[#39FF14]">{'>'} RECENT GRANTS</h2>
            <div className="space-y-1 text-xs">
              {history.map((g) => {
                const a = analytics[g.id];
                const loadingA = analyticsLoading[g.id];
                const sent = a?.sent || g.totals?.email_delivered || 0;
                const pct = (n) => (sent > 0 ? Math.round((n / sent) * 100) : 0);
                return (
                  <div key={g.id} className="border border-[#2A0F3D]/50 p-2" data-testid={`history-row-${g.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-[#A3C7A8]">
                        <Sparkles className="inline w-3 h-3 mr-1 text-[#39FF14]" />
                        {g.points_each} pts × {g.recipients_count} = {g.points_each * g.recipients_count} pts
                      </span>
                      <span className="text-[#6B8E73] text-[10px]">{new Date(g.sent_at).toLocaleString()}</span>
                    </div>
                    <p className="text-[#6B8E73] text-[10px] mt-1">
                      {g.reason} · {g.totals?.push_delivered || 0} push · {g.totals?.email_delivered || 0} email · by {g.actor_email}
                    </p>
                    {g.template_name && (
                      <p className="text-[#39FF14] text-[10px] mt-1" data-testid={`history-template-${g.id}`}>
                        <span className="text-[#6B8E73]">// from template:</span> {g.template_name}
                      </p>
                    )}
                    {/* Phase 6.4 — Show a "why failed?" button whenever this grant had recipients but the email delivery counts look short. */}
                    {g.email_sent && (g.totals?.email_delivered || 0) < (g.recipients_count || 0) && (
                      <div className="mt-2">
                        {!diagnostic[g.id] && (
                          <button
                            onClick={() => fetchDiagnostic(g.id)}
                            disabled={diagnosticLoading[g.id]}
                            className="text-[10px] text-[#FFB020] hover:text-[#FF3B30] uppercase tracking-wider border border-[#FFB020]/40 hover:border-[#FF3B30] px-2 py-0.5"
                            data-testid={`view-diagnostic-${g.id}`}
                          >
                            {diagnosticLoading[g.id] ? 'LOADING…' : `> WHY DID ${(g.recipients_count || 0) - (g.totals?.email_delivered || 0)} EMAILS FAIL?`}
                          </button>
                        )}
                        {diagnostic[g.id] && (
                          <div className="border border-[#FFB020]/40 bg-[#050003] p-2 mt-1 space-y-1" data-testid={`diagnostic-panel-${g.id}`}>
                            <p className="text-[10px] text-[#FFB020] uppercase tracking-wider">
                              // {diagnostic[g.id].email_failed || 0} email failed · {diagnostic[g.id].recipients_with_errors || 0} recipients with errors
                            </p>
                            {Object.keys(diagnostic[g.id].error_summary || {}).length === 0 ? (
                              <p className="text-[10px] text-[#A3C7A8]/60 italic">
                                No error strings recorded. Emails may have been skipped silently (e.g. missing address, or send was intentionally skipped).
                              </p>
                            ) : (
                              Object.entries(diagnostic[g.id].error_summary)
                                .sort((a, b) => b[1] - a[1])
                                .map(([msg, count]) => (
                                  <div key={msg} className="flex gap-2 text-[10px]" data-testid={`error-row-${g.id}`}>
                                    <span className="text-[#FF3B30] font-bold min-w-[32px] text-right">{count}×</span>
                                    <span className="text-[#A3C7A8] break-all">{msg}</span>
                                  </div>
                                ))
                            )}
                            <button
                              onClick={() => setDiagnostic((p) => { const n = { ...p }; delete n[g.id]; return n; })}
                              className="text-[9px] text-[#6B8E73] hover:text-[#39FF14] uppercase mt-1"
                              data-testid={`close-diagnostic-${g.id}`}
                            >
                              [x] close
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {g.email_sent && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {!a && (
                          <button
                            onClick={() => fetchAnalytics(g.id)}
                            disabled={loadingA}
                            className="text-[10px] text-[#7FD8FF] hover:text-[#39FF14] uppercase tracking-wider border border-[#2A0F3D] px-2 py-0.5 hover:border-[#7FD8FF]"
                            data-testid={`view-analytics-${g.id}`}
                          >
                            {loadingA ? 'LOADING…' : '> EMAIL ANALYTICS'}
                          </button>
                        )}
                        {a && a.found && (
                          <>
                            <span className="text-[10px] text-[#A3C7A8]/60">📨</span>
                            <span className="text-[10px] text-[#A3C7A8]">
                              <span className="text-[#39FF14]">{a.delivered}</span>/{a.sent} delivered
                            </span>
                            <span className="text-[#6B8E73] text-[10px]">·</span>
                            <span className="text-[10px] text-[#A3C7A8]">
                              <span className="text-[#7FD8FF]">{a.opened_unique}</span>/{a.sent} opened
                              <span className="text-[#6B8E73]"> ({pct(a.opened_unique)}%)</span>
                            </span>
                            <span className="text-[#6B8E73] text-[10px]">·</span>
                            <span className="text-[10px] text-[#A3C7A8]">
                              <span className="text-[#FFB020]">{a.clicked_unique}</span>/{a.sent} clicked
                              <span className="text-[#6B8E73]"> ({pct(a.clicked_unique)}%)</span>
                            </span>
                            {a.bounced > 0 && (
                              <>
                                <span className="text-[#6B8E73] text-[10px]">·</span>
                                <span className="text-[10px] text-[#FF3B30]">{a.bounced} bounced</span>
                              </>
                            )}
                            <button
                              onClick={() => fetchAnalytics(g.id)}
                              className="ml-2 text-[9px] text-[#A3C7A8]/50 hover:text-[#39FF14] uppercase"
                              data-testid={`refresh-analytics-${g.id}`}
                            >
                              ↻
                            </button>
                          </>
                        )}
                        {a && !a.found && (
                          <span className="text-[10px] text-[#FF3B30]">Grant not found</span>
                        )}
                        {a && a.found && a.note && (
                          <span className="text-[10px] text-[#A3C7A8]/50 italic">{a.note}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Phase 6.0.4 — Save-as-template dialog */}
      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent className="bg-[#0C0612] border border-[#39FF14] rounded-none text-[#39FF14] font-mono max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#39FF14] uppercase tracking-wider text-sm flex items-center gap-2">
              <BookmarkPlus className="w-4 h-4" /> SAVE AS TEMPLATE
            </DialogTitle>
            <DialogDescription className="text-[#6B8E73] text-xs">
              Saves the current Push + Email + CTA + Points fields. Reuse from the dropdown at the top.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-[#6B8E73] text-[10px] uppercase">Template name</Label>
            <Input
              value={saveTemplateName}
              onChange={(e) => setSaveTemplateName(e.target.value)}
              placeholder="e.g. Referral Push — DMV Lapsed"
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] rounded-none mt-1"
              maxLength={60}
              data-testid="save-template-name-input"
              onKeyDown={(e) => { if (e.key === 'Enter') saveAsTemplate(); }}
              autoFocus
            />
            <p className="text-[#6B8E73] text-[10px] mt-2">
              Channels included: <strong className="text-[#39FF14]">push{includeEmail ? ' + email' : ''}{points > 0 ? ' + points' : ''}</strong>
            </p>
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => setSaveTemplateOpen(false)}
              className="border border-[#2A0F3D] text-[#6B8E73] hover:border-[#39FF14] hover:text-[#39FF14] px-4 py-2 text-xs uppercase"
              data-testid="save-template-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveAsTemplate}
              disabled={savingTemplate || !saveTemplateName.trim()}
              className="bg-[#39FF14] text-[#050003] hover:bg-[#7FFF55] disabled:opacity-40 px-4 py-2 text-xs font-bold uppercase"
              data-testid="save-template-confirm"
            >
              {savingTemplate ? 'SAVING…' : 'SAVE'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
